import React from 'react'
import { scoreGuess } from './gameLogic'
import { cn } from '../../../lib/utils'

// Per-cell tone for the three Wordle states. Colours are explicit hex-
// adjacent Tailwind families (emerald/amber/stone) rather than the
// app-primary tokens so the board stays recognisably Wordle even when
// the rest of the app's primary tint shifts. Dark / OLED inherit via
// the `dark:` shade variants — both modes hit the same WCAG contrast
// against the white letter.
const TONE = {
  correct: 'bg-emerald-500 dark:bg-emerald-600 border-emerald-500 dark:border-emerald-600 text-white',
  present: 'bg-amber-500  dark:bg-amber-600  border-amber-500  dark:border-amber-600  text-white',
  absent:  'bg-stone-500  dark:bg-stone-600  border-stone-500  dark:border-stone-600  text-white',
  empty:   'bg-transparent border-border text-foreground',
  pending: 'bg-transparent border-foreground/50 text-foreground',
}

function Cell({ letter, state }) {
  return (
    <div
      className={cn(
        'w-14 h-14 sm:w-16 sm:h-16 rounded-md border-2 flex items-center justify-center',
        'text-2xl sm:text-3xl font-bold uppercase select-none',
        'transition-colors',
        TONE[state],
      )}
      dir="ltr"
    >
      {letter || ''}
    </div>
  )
}

// One row of the grid. Three shapes:
//   - Submitted: scored against the answer (correct/present/absent).
//   - In-progress: the row the user is currently typing into; renders
//     each filled cell as "pending" and empty cells as "empty".
//   - Empty: every cell "empty".
function Row({ guess, isCurrent, answer }) {
  const letters = (guess || '').padEnd(5).slice(0, 5).split('')
  const scored = guess && !isCurrent ? scoreGuess(guess, answer) : null
  return (
    <div className="flex gap-1.5 justify-center" dir="ltr">
      {letters.map((letter, i) => {
        let state
        if (scored) state = scored[i]
        else if (isCurrent && letter.trim()) state = 'pending'
        else state = 'empty'
        return <Cell key={i} letter={letter.trim()} state={state} />
      })}
    </div>
  )
}

// Full 6-row board. `guesses` is the array of submitted lowercase
// guesses; `currentGuess` is the typing-in-progress row (also lowercase).
// `gameOver` is the "stop showing the active row" flag.
export default function Board({ guesses, currentGuess, answer, gameOver }) {
  const rows = []
  for (let i = 0; i < 6; i++) {
    if (i < guesses.length) {
      rows.push(<Row key={i} guess={guesses[i]} answer={answer} isCurrent={false} />)
    } else if (i === guesses.length && !gameOver) {
      rows.push(<Row key={i} guess={currentGuess} answer={answer} isCurrent />)
    } else {
      rows.push(<Row key={i} guess="" answer={answer} isCurrent={false} />)
    }
  }
  // Lock the board to LTR even when the surrounding chrome is RTL
  // (Arabic UI). The Wordle answer pool is English in PR 1 — typing
  // "CRANE" should fill cells 1→5 left-to-right, regardless of the
  // page's text direction. PR 3's Arabic variant will flip this back
  // when an `ar` mode prop is added.
  return <div className="flex flex-col gap-1.5" dir="ltr">{rows}</div>
}
